/**
 * Provenance — the ISO 42001 evidence chain, expressed in the type system.
 *
 * CR-01 requires that for any fact the system asserts you can show **which model
 * produced it and whether a human has verified it**, with the extraction
 * `schema_version` that governed the extraction. That pairing *is* the evidence.
 *
 * So this package does not carry a bare fact anywhere. Every extracted value is
 * wrapped by {@link provenanced}, which makes `{ value }` without
 * `{ provenance }` a compile error rather than a review comment. If you find
 * yourself wanting to unwrap one to save a few keystrokes in the front end,
 * that is the control working.
 */
import { z } from 'zod';
import { ProviderIdSchema } from './vocabulary.js';
import { CorrelationIdSchema, IsoTimestampUtcSchema, OpaqueIdSchema, UpnSchema, } from './primitives.js';
/* -------------------------------------------------------------------------- */
/* Human verification — FR-07                                                  */
/* -------------------------------------------------------------------------- */
/**
 * Whether a human has verified this field, discriminated on
 * `is_human_verified` so that a verified state **cannot** be represented without
 * a verifier and a timestamp. That is the second half of the CR-01 pairing; a
 * nullable `verified_by_upn` would let the evidence go missing silently.
 *
 * ## `AutoApproved` is settled, and is NOT human-verified (CLM-108, CR-06)
 *
 * The `false` member is itself a discriminated union on `verification_status`:
 * `Pending | Rejected` as before, and `AutoApproved` — a field the policy
 * settled because its measured accuracy met a co-signed threshold. Its
 * evidence is an `AutoApproval` audit row (`auto_approval_audit_id`), never a
 * `HumanVerification` row, so it carries **no verifier** and
 * `is_human_verified` stays `false`. It is never high stakes (D-2.1-5). Nested,
 * rather than a third flat member, so every consumer that switches on
 * `verification_status` under `is_human_verified: false` must handle it at
 * compile time — a fifth status silently rendered as `Pending`, or as
 * "Verified", is the failure D-2.1-4 exists to prevent.
 */
export const HumanVerificationSchema = z.discriminatedUnion('is_human_verified', [
    z.discriminatedUnion('verification_status', [
        z.object({
            is_human_verified: z.literal(false),
            verification_status: z.enum(['Pending', 'Rejected']),
            /** Addresses this field in the verification queue. Absent if not queued. */
            verification_id: OpaqueIdSchema.nullable(),
            /** FR-07 — drives queue ordering and the aging escalation. */
            is_high_stakes: z.boolean(),
            enqueued_utc: IsoTimestampUtcSchema.nullable(),
            /** Set by the aging signal (`vw_verification_aging`), not by a user. */
            escalated_utc: IsoTimestampUtcSchema.nullable(),
            /** Refused, not stripped: a row carrying policy evidence is not an outstanding row. */
            auto_approved_utc: z.never().optional(),
            auto_approval_audit_id: z.never().optional(),
        }),
        z.object({
            is_human_verified: z.literal(false),
            /** CLM-108 / CR-06 — settled by policy, not by a person. Terminal. */
            verification_status: z.literal('AutoApproved'),
            verification_id: OpaqueIdSchema,
            /** D-2.1-5 — a high-stakes field is always reviewed by a person, so never this. */
            is_high_stakes: z.literal(false),
            /** The `AutoApproval` audit row's `occurred_at_utc`. */
            auto_approved_utc: IsoTimestampUtcSchema,
            /** The `AutoApproval` audit row — the policy half of the CR-01 pair. A decimal string, as every audit id on the wire. */
            auto_approval_audit_id: z.string().regex(/^\d{1,19}$/, 'expected an audit id'),
        }),
    ]),
    z.object({
        is_human_verified: z.literal(true),
        /**
         * `Verified` — the extracted value was right. `Corrected` — a human changed
         * it. Keep them apart: the correction rate is how NFR-12 extraction accuracy
         * gets measured.
         */
        verification_status: z.enum(['Verified', 'Corrected']),
        verification_id: OpaqueIdSchema,
        is_high_stakes: z.boolean(),
        verified_by_upn: UpnSchema,
        verified_utc: IsoTimestampUtcSchema,
    }),
]);
/* -------------------------------------------------------------------------- */
/* Field provenance                                                            */
/* -------------------------------------------------------------------------- */
/**
 * Where one field's value came from.
 *
 * Discriminated on `is_ai_derived` so that `extraction_schema_version`, the
 * provider and the model are **structurally required** whenever a model produced
 * the value, and structurally absent when one did not. An optional
 * `schema_version` would be the easiest field in this system to forget, and it is
 * the one an auditor asks for first.
 */
export const FieldProvenanceSchema = z.discriminatedUnion('is_ai_derived', [
    z.object({
        is_ai_derived: z.literal(true),
        /** CR-01/CR-05 — the governed artefact version that produced this value. */
        extraction_schema_version: z.string().min(1).max(20),
        provider_id: ProviderIdSchema,
        /** Model identifier as reported by the provider, e.g. an Opus 5 model id. */
        model: z.string().min(1).max(120),
        extracted_at_utc: IsoTimestampUtcSchema,
        verification: HumanVerificationSchema,
    }),
    z.object({
        is_ai_derived: z.literal(false),
        /**
         * `manual_entry` — typed by a person, including every fact on a
         * `processing_restricted` contract (§4C).
         * `intake_metadata` — known before any reading happened: the contract key
         * from the DocuSign envelope, the party from the guided upload form. This is
         * what makes FR-29 deterministic rather than inferential (§4A.1).
         * `system_derived` — computed by the system, e.g. a renewal notice date
         * derived from an expiry date and a notice period.
         */
        origin: z.enum(['manual_entry', 'intake_metadata', 'system_derived']),
        /** The person who entered it; `null` for `system_derived`. */
        entered_by_upn: UpnSchema.nullable(),
        recorded_at_utc: IsoTimestampUtcSchema,
        verification: HumanVerificationSchema,
    }),
]);
/**
 * Wraps a value with its provenance.
 *
 * Use this for every field the extraction pipeline can populate. Fields set only
 * by intake or by the system — `contract_key`, `intake_path`, `created_utc` —
 * do not need it.
 *
 * ```ts
 * expiry_date: provenanced(IsoDateSchema.nullable())
 * // → { value: string | null; provenance: FieldProvenance }
 * ```
 */
export function provenanced(value) {
    return z.object({ value, provenance: FieldProvenanceSchema });
}
/* -------------------------------------------------------------------------- */
/* Conversion provenance — §4B                                                 */
/* -------------------------------------------------------------------------- */
/**
 * How the document text was produced, and — in the fallback case — who approved
 * losing FR-29.
 *
 * Pipeline order is load-bearing: **intake → convert → pseudonymise → extract**
 * (§2.2). Conversion must precede pseudonymisation because substitution operates
 * on *text*; you cannot string-replace inside a PDF.
 *
 * The `pdf_native_fallback` member therefore *requires* an approver. If
 * conversion fails there is no text to substitute, so FR-29 does not run — which
 * makes the fallback a compliance decision, not a technical one (CLAUDE.md rule
 * 3). Making the approval a required field means an unapproved silent fallback
 * cannot be represented at all.
 */
export const ConversionProvenanceSchema = z.discriminatedUnion('conversion_method', [
    z.object({
        conversion_method: z.literal('text_layer'),
        converted_utc: IsoTimestampUtcSchema,
        /** §4B.2 quality gate — chars/page plausibility. */
        chars_per_page: z.number().min(0).nullable(),
        tables_detected: z.boolean(),
    }),
    z.object({
        conversion_method: z.literal('ocr'),
        converted_utc: IsoTimestampUtcSchema,
        chars_per_page: z.number().min(0).nullable(),
        tables_detected: z.boolean(),
        /** Why the text layer was rejected — scan detected, quality gate failed. */
        ocr_reason: z.string().min(1).max(200),
    }),
    z.object({
        conversion_method: z.literal('pdf_native_fallback'),
        converted_utc: IsoTimestampUtcSchema,
        /**
         * Always `false`, and structurally so. There is no text to substitute into,
         * so FR-29 cannot have run.
         */
        fr29_pseudonymisation_applied: z.literal(false),
        approved_by_upn: UpnSchema,
        approved_utc: IsoTimestampUtcSchema,
        approval_reason: z.string().min(1).max(1000),
        /** Audit row evidencing the approval, so the decision is reachable. */
        approval_audit_id: OpaqueIdSchema,
    }),
]);
/**
 * CLM-85 / R-85-1 — the quality caveats a PASSING conversion may carry. Provenance,
 * not a fact: written by the runner with `conversion_method` before the AI boundary,
 * cleared with it by a re-run, never read by extraction, so no `SCHEMA_VERSION` bump.
 *
 * `tables_missing` — tables were expected (a SOW rate card) and the converter found
 * none while text density was fine. The gate used to refuse the document for this
 * (`expected_tables_missing`); since CLM-85 extraction proceeds and the verifier is
 * told where to look. Single source for `@clm/conversion` (`QUALITY_CAVEATS`) and
 * for `clm.contract.conversion_caveat` (`0093`, `ck_contract_conversion_caveat`) —
 * shared-types owns it so conversion → shared-types stays a one-way dependency.
 */
export const CONVERSION_CAVEATS = ['tables_missing'];
/** CLM-85 / R-85-1 — a quality caveat on a PASSING conversion. Provenance, not a fact. */
export const ConversionCaveatSchema = z.enum(CONVERSION_CAVEATS);
/* -------------------------------------------------------------------------- */
/* AI processing provenance — §4A, §4C                                         */
/* -------------------------------------------------------------------------- */
/** Residue found after substitution, before sending — §4A.5 leak detector. */
export const LeakDetectorResultSchema = z.object({
    residual_hit_count: z.number().int().min(0),
    /** True when the send was held for review rather than proceeding. */
    send_blocked: z.boolean(),
    /** Terms that survived substitution, so an admin can add the missing alias. */
    residual_terms: z.array(z.string().min(1).max(200)),
    reviewed_by_upn: UpnSchema.nullable(),
    reviewed_utc: IsoTimestampUtcSchema.nullable(),
});
/**
 * What happened at the AI boundary for one contract.
 *
 * The boundary is crossed **once per contract, at ingest** (§4A.9). No search,
 * ranking, notification or reporting operation sends content externally, which
 * is why this appears on a contract and nowhere near `search`.
 */
export const AiProcessingProvenanceSchema = z.discriminatedUnion('ai_processing_state', [
    z.object({
        ai_processing_state: z.literal('pending'),
    }),
    z.object({
        /**
         * §4C — the contract's own terms forbid third-party processing. It is still
         * stored, converted in-tenant, indexed and searchable; facts are entered
         * manually. Only the *reasoning* step is withheld. It must never reach an LLM.
         */
        ai_processing_state: z.literal('excluded_processing_restricted'),
        /**
         * Decision 3: the flag is set at *party* level in Qfly and inherited, with a
         * per-contract override — Legal decides once per client, not once per
         * document.
         */
        restriction_source: z.enum(['party_inherited', 'contract_override']),
        restriction_reason: z.string().min(1).max(1000).nullable(),
    }),
    z.object({
        ai_processing_state: z.literal('completed'),
        /** FR-29 — the versioned substitution map that produced the payload. */
        pseudonym_map_version: z.string().min(1).max(40),
        leak_detector: LeakDetectorResultSchema,
        extraction_schema_version: z.string().min(1).max(20),
        provider_id: ProviderIdSchema,
        model: z.string().min(1).max(120),
        extracted_at_utc: IsoTimestampUtcSchema,
        /**
         * CR-02 control 4 — the uploaded document was explicitly deleted at the
         * processor.
         *
         * Structurally `true`: an extraction whose deletion could not be evidenced is
         * not a `completed` extraction, it is a `failed` one. §4A.7 is explicit that
         * the value of this record is being able to evidence precisely what crossed
         * the boundary, and a `boolean` here would let "we think so" be recorded as
         * evidence.
         */
        remote_document_deleted: z.literal(true),
    }),
    z.object({
        ai_processing_state: z.literal('refused'),
        pseudonym_map_version: z.string().min(1).max(40),
        refusal_category: z.string().min(1).max(120).nullable(),
        refused_at_utc: IsoTimestampUtcSchema,
        /**
         * Structurally `true`. A refusal routes to **human review**, never to an
         * automatic fallback model (CLAUDE.md rule 6): CR-01 requires the evidence
         * chain to show which model produced which field, and a silent re-serve
         * breaks it.
         */
        routed_to_human_review: z.literal(true),
    }),
    z.object({
        ai_processing_state: z.literal('failed'),
        failure_reason: z.string().min(1).max(1000),
        failed_at_utc: IsoTimestampUtcSchema,
        attempt_count: z.number().int().min(1),
    }),
]);
/* -------------------------------------------------------------------------- */
/* Audit receipt — NFR-07                                                      */
/* -------------------------------------------------------------------------- */
/**
 * The audit row a mutation wrote, returned to the caller.
 *
 * Every mutating endpoint in this contract returns one. That is deliberate:
 * NFR-07 requires an audit row on every mutation, and putting the receipt in the
 * response type means an endpoint that forgot to write one cannot satisfy its own
 * contract.
 */
export const AuditReceiptSchema = z.object({
    audit_id: OpaqueIdSchema,
    occurred_at_utc: IsoTimestampUtcSchema,
    actor_upn: UpnSchema.nullable(),
    /**
     * Captured **at action time**, not resolved later — Entra group membership
     * drifts, and an audit row that reads today's roles onto last year's action is
     * not evidence.
     */
    actor_roles: z.array(z.string().min(1).max(80)),
    correlation_id: CorrelationIdSchema,
    /** Present when the mutation touched an AI-derived value (CR-01). */
    extraction_schema_version: z.string().min(1).max(20).nullable(),
});
//# sourceMappingURL=provenance.js.map